import { useCallback, useMemo, useState } from 'react';

import { ESectionOfferType } from 'constants/enums';
import { useFormikContext } from 'formik';

import type { InputSelectDropdownOption } from 'components/InputSelectDropdown/InputSelectDropdown';

import { SectionsItem } from '../../StoreTheme/storeTheme.types';

import type { StoreSectionsFormValues } from './sectionsSectionFields.types';

export const SECTION_TYPE_OPTIONS: InputSelectDropdownOption[] = [
  { value: ESectionOfferType.BUNDLE, label: 'Bundle' },
  { value: ESectionOfferType.PROMOTION, label: 'Promotion' }
];

const emptyDraft = {
  name: '',
  publisherSectionId: '',
  offerType: ESectionOfferType.BUNDLE as ESectionOfferType,
  image: ''
};

const SECTIONS_LIST_KEYS = {
  bundle: 'bundleSections',
  promotion: 'promotionSections'
} as const;

type SectionsListFormKey =
  (typeof SECTIONS_LIST_KEYS)[keyof typeof SECTIONS_LIST_KEYS];

const getSectionListKeyForId = (
  publisherSectionId: string,
  bundleSections: SectionsItem[] | undefined,
  promotionSections: SectionsItem[] | undefined
): SectionsListFormKey => {
  const bundle = bundleSections || [];
  const promo = promotionSections || [];
  if (bundle.some((s) => s.publisherSectionId === publisherSectionId)) {
    return SECTIONS_LIST_KEYS.bundle;
  }
  if (promo.some((s) => s.publisherSectionId === publisherSectionId)) {
    return SECTIONS_LIST_KEYS.promotion;
  }
  return SECTIONS_LIST_KEYS.bundle;
};

export function useSectionsDraft() {
  const { values, setFieldValue } =
    useFormikContext<StoreSectionsFormValues>();
  const [draft, setDraft] = useState(emptyDraft);
  const [editedSectionId, setEditedSectionId] = useState('');

  const allSections = useMemo(
    () =>
      (values.bundleSections || [])
        .concat(values.promotionSections || [])
        .filter((s) => !s.toBeDeleted),
    [values.bundleSections, values.promotionSections]
  );

  const nameError = useMemo(() => {
    if (
      draft.name &&
      allSections.some(
        (item) =>
          item.publisherSectionId !== editedSectionId &&
          item.name === draft.name
      )
    ) {
      return 'Section name already exists';
    }
    return '';
  }, [draft.name, allSections, editedSectionId]);

  const idError = useMemo(() => {
    if (draft.publisherSectionId?.includes(' ')) {
      return 'Section ID should not contain spaces';
    }
    if (
      draft.publisherSectionId &&
      draft.publisherSectionId !== editedSectionId &&
      allSections.some(
        (item) => item.publisherSectionId === draft.publisherSectionId
      )
    ) {
      return 'Section ID already exists';
    }
    return '';
  }, [draft.publisherSectionId, allSections, editedSectionId]);

  const listKeyForOfferType = useCallback(
    (offerType?: ESectionOfferType) =>
      offerType === ESectionOfferType.PROMOTION
        ? SECTIONS_LIST_KEYS.promotion
        : SECTIONS_LIST_KEYS.bundle,
    []
  );

  const addOrSaveSection = useCallback(() => {
    if (!draft.publisherSectionId?.trim() || !draft.name?.trim()) return;
    if (idError || nameError) return;

    const sectionPayload: SectionsItem = {
      _id: draft.publisherSectionId,
      publisherSectionId: draft.publisherSectionId,
      name: draft.name,
      offerType: draft.offerType
    };

    if (editedSectionId) {
      const prevRow = [
        ...(values.bundleSections || []),
        ...(values.promotionSections || [])
      ].find((s) => s.publisherSectionId === editedSectionId);
      if (prevRow?.sectionId) {
        sectionPayload.sectionId = prevRow.sectionId;
      }
      if (prevRow?.toAdd) {
        sectionPayload.toAdd = true;
      } else {
        sectionPayload.toBeEdited = true;
      }

      if (draft.image) {
        sectionPayload.image = draft.image;
      } else if (prevRow?.image) {
        sectionPayload.image = '';
      }

      const prevKey = getSectionListKeyForId(
        editedSectionId,
        values.bundleSections,
        values.promotionSections
      );
      const nextKey = listKeyForOfferType(draft.offerType);

      const removeFrom = (list: SectionsItem[]) =>
        list.filter((s) => s.publisherSectionId !== editedSectionId);

      let bundle = [...(values.bundleSections || [])];
      let promotion = [...(values.promotionSections || [])];

      if (prevKey === SECTIONS_LIST_KEYS.bundle) {
        bundle = removeFrom(bundle);
      } else {
        promotion = removeFrom(promotion);
      }

      if (nextKey === SECTIONS_LIST_KEYS.bundle) {
        bundle = [...bundle, sectionPayload];
      } else {
        promotion = [...promotion, sectionPayload];
      }

      setFieldValue(SECTIONS_LIST_KEYS.bundle, bundle);
      setFieldValue(SECTIONS_LIST_KEYS.promotion, promotion);
      setEditedSectionId('');
    } else {
      if (draft.image) {
        sectionPayload.image = draft.image;
      }
      sectionPayload.toAdd = true;
      const key = listKeyForOfferType(draft.offerType);
      const list =
        key === SECTIONS_LIST_KEYS.promotion
          ? values.promotionSections || []
          : values.bundleSections || [];
      setFieldValue(key, [...list, sectionPayload]);
    }

    setDraft(emptyDraft);
  }, [
    draft,
    editedSectionId,
    idError,
    nameError,
    listKeyForOfferType,
    setFieldValue,
    values.bundleSections,
    values.promotionSections
  ]);

  const removeRow = useCallback(
    (id: string) => {
      if (id === editedSectionId) {
        setEditedSectionId('');
        setDraft(emptyDraft);
      }
      const key = getSectionListKeyForId(
        id,
        values.bundleSections,
        values.promotionSections
      );
      const sections =
        key === SECTIONS_LIST_KEYS.promotion
          ? values.promotionSections || []
          : values.bundleSections || [];
      const existing = sections.find(
        (section: SectionsItem) =>
          section.publisherSectionId === id &&
          (Boolean(section.sectionId) || !section.toAdd)
      );
      const updated = existing
        ? sections.map((section: SectionsItem) =>
            section.publisherSectionId === id
              ? { ...section, toBeDeleted: true }
              : section
          )
        : sections.filter((section) => section.publisherSectionId !== id);
      setFieldValue(key, updated);
    },
    [
      editedSectionId,
      setFieldValue,
      values.bundleSections,
      values.promotionSections
    ]
  );

  const startEdit = useCallback((row: SectionsItem) => {
    setEditedSectionId(row.publisherSectionId);
    setDraft({
      name: row.name,
      publisherSectionId: row.publisherSectionId,
      offerType: row.offerType || ESectionOfferType.BUNDLE,
      image: row.image || ''
    });
  }, []);

  const sectionTypeSelected = useMemo(
    () =>
      SECTION_TYPE_OPTIONS.find((o) => o.value === draft.offerType) ??
      SECTION_TYPE_OPTIONS[0],
    [draft.offerType]
  );

  const tableRows = useMemo(() => {
    const bundles = (values.bundleSections || []).filter((s) => !s.toBeDeleted);
    const promos = (values.promotionSections || []).filter(
      (s) => !s.toBeDeleted
    );
    return [...bundles, ...promos];
  }, [values.bundleSections, values.promotionSections]);

  const addDisabled =
    !draft.publisherSectionId?.trim() ||
    !draft.name?.trim() ||
    Boolean(idError) ||
    Boolean(nameError);

  return {
    draft,
    setDraft,
    editedSectionId,
    idError,
    nameError,
    sectionTypeSelected,
    addOrSaveSection,
    removeRow,
    startEdit,
    tableRows,
    addDisabled
  };
}
